import { motion } from 'framer-motion';
import { fadeInUp } from '../lib/animations';
import SEOPageLayout from '../components/seo/SEOPageLayout';

const relatedLinks = [
  { to: '/retrouver-information-entreprise', label: 'Pourquoi retrouver une information prend autant de temps' },
  { to: '/productivite-equipe-outil', label: 'Comment vos équipes perdent 30% de leur temps' },
];

const PerteInformation = () => {
  return (
    <SEOPageLayout
      title="Ce que la perte d'information coûte réellement à votre entreprise"
      metaTitle="Perte d'information en entreprise : le coût réel | Cloneia"
      metaDescription="La perte d'information en entreprise a un coût invisible mais réel. Comprenez l'impact et découvrez une nouvelle approche pour y remédier."
      url="/perte-information-entreprise"
      readingTime={5}
      subtitle="Chaque jour, votre entreprise oublie ce qu'elle sait."
      relatedLinks={relatedLinks}
    >
      <article className="space-y-12 text-[#a1a1aa] leading-relaxed" data-testid="seo-article-perte">

        {/* Section 1 */}
        <motion.div {...fadeInUp}>
          <h2 className="text-2xl font-semibold text-[#f5f5f5] mb-4">
            L'information qui disparaît
          </h2>
          <p>
            Chaque jour, vos collaborateurs échangent des dizaines d'emails, passent des appels, envoient des messages. Chaque échange contient de l'information précieuse.
          </p>
          <p className="mt-4">
            Et chaque jour, une grande partie de cette information disparaît. Pas parce qu'elle est supprimée. Mais parce que personne ne la retrouve.
          </p>
          <p className="mt-4 text-[#f5f5f5] font-medium">
            La perte d'information n'est pas un accident. C'est un phénomène permanent.
          </p>
        </motion.div>

        {/* Section 2 */}
        <motion.div {...fadeInUp}>
          <h2 className="text-2xl font-semibold text-[#f5f5f5] mb-4">
            Les trois causes principales
          </h2>
          <div className="space-y-6">
            <div className="p-6 rounded-xl border border-white/5 bg-[#1a1a2e]/30">
              <h3 className="text-[#f5f5f5] font-medium mb-2">La mémoire humaine</h3>
              <p>
                Un collaborateur retient environ 10% de ce qu'il a entendu la semaine précédente. Le reste est perdu. Définitivement.
              </p>
            </div>
            <div className="p-6 rounded-xl border border-white/5 bg-[#1a1a2e]/30">
              <h3 className="text-[#f5f5f5] font-medium mb-2">Le turnover</h3>
              <p>
                Quand un collaborateur part, il emporte avec lui des mois, parfois des années de connaissance. Des relations clients, des historiques, des savoir-faire.
              </p>
            </div>
            <div className="p-6 rounded-xl border border-white/5 bg-[#1a1a2e]/30">
              <h3 className="text-[#f5f5f5] font-medium mb-2">La dispersion</h3>
              <p>
                L'information existe, mais elle est éparpillée dans tellement d'endroits qu'elle est pratiquement invisible.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Section 3 */}
        <motion.div {...fadeInUp}>
          <h2 className="text-2xl font-semibold text-[#f5f5f5] mb-4">
            Le coût réel pour votre entreprise
          </h2>
          <p>
            Ce n'est pas un coût qui apparaît sur un bilan. Mais il est bien réel.
          </p>
          <div className="mt-6 space-y-4">
            <p>
              <span className="text-[#f5f5f5] font-medium">Du temps perdu</span> — vos équipes passent des heures à chercher, redemander, reconstituer des informations qui existaient déjà.
            </p>
            <p>
              <span className="text-[#f5f5f5] font-medium">Des erreurs évitables</span> — des décisions prises sans avoir toutes les données. Des engagements oubliés. Des clients mal suivis.
            </p>
            <p>
              <span className="text-[#f5f5f5] font-medium">Des opportunités manquées</span> — un devis non relancé, un contact perdu, une information stratégique jamais partagée.
            </p>
          </div>
        </motion.div>

        {/* Section 4 */}
        <motion.div {...fadeInUp}>
          <h2 className="text-2xl font-semibold text-[#f5f5f5] mb-4">
            Pourquoi ça ne s'arrange pas tout seul
          </h2>
          <p>
            Plus votre entreprise grandit, plus le problème s'aggrave. Plus de collaborateurs signifie plus d'échanges, plus de dispersion, plus d'oubli.
          </p>
          <p className="mt-4">
            Les outils classiques ne résolvent pas ce problème. Ils créent des silos supplémentaires. Chaque nouvel outil est un nouvel endroit où l'information peut se perdre.
          </p>
          <p className="mt-4 text-[#f5f5f5] font-medium">
            La solution n'est pas d'ajouter un outil de plus. C'est de changer la façon dont l'information est captée et accessible.
          </p>
        </motion.div>

        {/* Section 5 - Cloneia */}
        <motion.div {...fadeInUp}>
          <h2 className="text-2xl font-semibold text-[#f5f5f5] mb-4">
            Préserver la mémoire de votre entreprise
          </h2>
          <p>
            Cloneia crée un clone mémoire de chaque collaborateur. Toute l'information de ses échanges — appels, emails, messages — est automatiquement captée et conservée.
          </p>
          <p className="mt-4">
            Plus rien ne se perd. Plus rien ne dépend de la mémoire d'une seule personne.
          </p>
          <p className="mt-4 text-[#f5f5f5] font-medium">
            Un collaborateur part ? Sa connaissance reste. Un détail oublié ? Le clone s'en souvient.
          </p>
        </motion.div>

        {/* Section 6 */}
        <motion.div {...fadeInUp}>
          <h2 className="text-2xl font-semibold text-[#f5f5f5] mb-4">
            La différence avec un outil classique
          </h2>
          <p>
            Un CRM demande qu'on le remplisse. Un drive demande qu'on y range des documents. Un outil de notes demande qu'on écrive.
          </p>
          <p className="mt-4">
            Cloneia ne demande rien à personne. L'information est captée automatiquement, là où elle se trouve déjà : dans les échanges naturels de vos équipes.
          </p>
          <p className="mt-4 text-[#f5f5f5] font-medium">
            Zéro saisie. Zéro effort. L'information est préservée sans que personne n'ait à y penser.
          </p>
        </motion.div>

      </article>
    </SEOPageLayout>
  );
};

export default PerteInformation;
